import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Avatar,
    Badge,
    Box,
    ClickAwayListener,
    CircularProgress,
    Divider,
    Fab,
    IconButton,
    List,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
    Paper,
    Popper,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { io } from 'socket.io-client';
import { API_BASE_URL, tryPaths } from './api';

const ROSTER_PATHS = ['/team/messenger/roster', '/api/team/messenger/roster'];
const CONVERSATIONS_PATHS = ['/team/messenger/conversations', '/api/team/messenger/conversations'];

function decodeJwtPayload(token) {
    try {
        const raw = String(token || '').trim();
        const parts = raw.split('.');
        if (parts.length < 2) return null;
        const b64url = parts[1];
        const b64 = String(b64url).replace(/-/g, '+').replace(/_/g, '/');
        const pad = b64.length % 4;
        const padded = pad ? (b64 + '='.repeat(4 - pad)) : b64;
        const json = atob(padded);
        return JSON.parse(json);
    } catch {
        return null;
    }
}

function getMyUserIdFromToken(token) {
    const payload = decodeJwtPayload(token);
    const n = Number(payload?.sub ?? payload?.user_id ?? payload?.id);
    return Number.isFinite(n) ? n : null;
}

function getAuthToken() {
    try {
        return String(localStorage.getItem('monsurv_auth_token') || '').trim();
    } catch {
        return '';
    }
}

function authHeaders() {
    const token = getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
}

function safeName(user) {
    const name = String(user?.display_name || user?.username || user?.email || '').trim();
    return name || 'User';
}

function initials(name) {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] || '') : '';
    const out = `${first}${last}`.toUpperCase();
    return out || '?';
}

function formatTimeShort(value) {
    if (!value) return '';
    try {
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
        return '';
    }
}

function computeAvoidFabBottomPx(defaultBottom = 32) {
    try {
        const els = Array.from(document.querySelectorAll('[data-ms-fab="add"]'));
        let best = defaultBottom;
        const gap = 16;
        for (const el of els) {
            if (!el) continue;
            const style = window.getComputedStyle(el);
            if (style.display === 'none' || style.visibility === 'hidden') continue;
            const rect = el.getBoundingClientRect();
            if (!rect || rect.height <= 0 || rect.width <= 0) continue;
            const distanceFromBottom = Math.max(0, window.innerHeight - rect.bottom);
            const candidate = Math.round(distanceFromBottom + rect.height + gap);
            if (candidate > best) best = candidate;
        }
        return best;
    } catch {
        return defaultBottom;
    }
}

function useFabAvoidanceBottom(defaultBottom = 32) {
    const [bottom, setBottom] = useState(() => defaultBottom);

    useEffect(() => {
        let raf = 0;
        const recalc = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => setBottom(computeAvoidFabBottomPx(defaultBottom)));
        };

        recalc();
        window.addEventListener('resize', recalc);
        window.addEventListener('scroll', recalc, { passive: true });

        const mo = new MutationObserver(recalc);
        try {
            mo.observe(document.body, { subtree: true, childList: true, attributes: true });
        } catch {
            // ignore
        }

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', recalc);
            window.removeEventListener('scroll', recalc);
            try { mo.disconnect(); } catch { }
        };
    }, [defaultBottom]);

    return bottom;
}

async function apiGet(paths, urlBuilder) {
    return await tryPaths(
        paths,
        (path) => urlBuilder(path),
        { method: 'GET', headers: { ...authHeaders() } }
    );
}

async function apiPost(paths, urlBuilder, body) {
    return await tryPaths(
        paths,
        (path) => urlBuilder(path),
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify(body || {})
        }
    );
}

function buildUserMessagesPaths(otherUserId) {
    const id = encodeURIComponent(String(otherUserId));
    return [`/team/messenger/users/${id}/messages`, `/api/team/messenger/users/${id}/messages`];
}

function buildUserSendMessagePaths(otherUserId) {
    const id = encodeURIComponent(String(otherUserId));
    return [`/team/messenger/users/${id}/messages`, `/api/team/messenger/users/${id}/messages`];
}

function buildUserReadPaths(otherUserId) {
    const id = encodeURIComponent(String(otherUserId));
    return [`/team/messenger/users/${id}/read`, `/api/team/messenger/users/${id}/read`];
}

export default function TeamMessengerWidget() {
    const token = getAuthToken();
    const myUserId = useMemo(() => getMyUserIdFromToken(token), [token]);
    const hasSession = Boolean(token) && Number.isFinite(Number(myUserId));
    const [open, setOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    const [rosterLoading, setRosterLoading] = useState(false);
    const [rosterError, setRosterError] = useState('');
    const [roster, setRoster] = useState(() => []);

    const [convLoading, setConvLoading] = useState(false);
    const [convError, setConvError] = useState('');
    const [conversations, setConversations] = useState(() => []);

    const [activeUserId, setActiveUserId] = useState(null);

    const [threadLoading, setThreadLoading] = useState(false);
    const [threadError, setThreadError] = useState('');
    const [thread, setThread] = useState(() => ({
        conversation_id: null,
        other_user: null,
        my_last_read_message_id: 0,
        other_last_read_message_id: 0,
        items: []
    }));

    const [draft, setDraft] = useState('');
    const [typingFromOther, setTypingFromOther] = useState(false);

    const typingTimeoutRef = useRef(null);
    const lastTypingEmitRef = useRef(0);
    const socketRef = useRef(null);

    const myUserIdRef = useRef(null);

    const activeUserIdRef = useRef(null);
    const threadConversationIdRef = useRef(null);

    const scrollRef = useRef(null);

    const bottom = useFabAvoidanceBottom(32);

    function closeChat() {
        setOpen(false);
        setAnchorEl(null);
        setTypingFromOther(false);
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    }

    const rosterById = useMemo(() => {
        const map = new Map();
        for (const u of roster || []) {
            if (u?.user_id) map.set(Number(u.user_id), u);
        }
        return map;
    }, [roster]);

    const convByOtherId = useMemo(() => {
        const map = new Map();
        for (const c of conversations || []) {
            const otherId = Number(c?.other_user?.user_id);
            if (Number.isFinite(otherId)) map.set(otherId, c);
        }
        return map;
    }, [conversations]);

    const activeUser = useMemo(() => {
        if (!activeUserId) return null;
        return rosterById.get(Number(activeUserId)) || convByOtherId.get(Number(activeUserId))?.other_user || null;
    }, [activeUserId, rosterById, convByOtherId]);

    useEffect(() => {
        myUserIdRef.current = Number.isFinite(Number(myUserId)) ? Number(myUserId) : null;
    }, [myUserId]);

    useEffect(() => {
        activeUserIdRef.current = activeUserId ? Number(activeUserId) : null;
    }, [activeUserId]);

    useEffect(() => {
        threadConversationIdRef.current = thread?.conversation_id ? Number(thread.conversation_id) : null;
    }, [thread?.conversation_id]);

    useEffect(() => {
        if (!open) return;
        try {
            const el = scrollRef.current;
            if (el) el.scrollTop = el.scrollHeight;
        } catch {
            // ignore
        }
    }, [open, thread.items?.length]);

    useEffect(() => {
        if (!open) return;
        loadRoster();
        loadConversations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!token) return;
        if (socketRef.current) return;

        // Keep unread counts fresh even when the chat window is closed.
        loadConversations({ silent: true });

        const s = io(API_BASE_URL, {
            transports: ['websocket'],
            auth: { token }
        });
        socketRef.current = s;

        s.on('presence:change', (payload) => {
            const uid = Number(payload?.user_id);
            const online = Boolean(payload?.online);
            if (!Number.isFinite(uid)) return;

            setRoster((prev) => (prev || []).map((u) => (Number(u.user_id) === uid ? { ...u, online } : u)));
            setConversations((prev) => (prev || []).map((c) => {
                const otherId = Number(c?.other_user?.user_id);
                if (otherId !== uid) return c;
                return { ...c, other_user: { ...(c.other_user || {}), online } };
            }));
        });

        s.on('typing', (payload) => {
            const fromId = Number(payload?.from_user_id);
            const isTyping = Boolean(payload?.is_typing);
            if (!Number.isFinite(fromId)) return;
            const activeOtherId = activeUserIdRef.current;
            if (!activeOtherId || fromId !== Number(activeOtherId)) return;

            setTypingFromOther(isTyping);
            if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
            if (isTyping) {
                typingTimeoutRef.current = setTimeout(() => setTypingFromOther(false), 2500);
            }
        });

        s.on('message:new', (payload) => {
            const msg = payload?.message;
            if (!msg?.id) return;

            const conversationId = Number(payload?.conversation_id);
            const senderId = Number(msg.sender_user_id);
            const meId = myUserIdRef.current;

            const otherId = activeUserIdRef.current;
            const activeConvId = threadConversationIdRef.current;
            if (otherId && Number(activeConvId) === conversationId) {
                setThread((prev) => {
                    const existing = (prev?.items || []).some((m) => Number(m.id) === Number(msg.id));
                    if (existing) return prev;
                    return { ...prev, items: [...(prev.items || []), msg] };
                });

                // Auto-mark as read when the thread is open and the message is from the other user.
                if (senderId === otherId) {
                    setTimeout(() => {
                        markRead(otherId, Number(msg.id));
                    }, 0);
                }
            }

            // Optimistically bump unread for incoming messages (helps UI update instantly).
            // Backend remains the source of truth; we also refresh conversations below.
            const isFromMe = Number.isFinite(Number(meId)) && Number(senderId) === Number(meId);
            const isActiveThread = otherId && Number(otherId) === Number(senderId) && Number(activeConvId) === conversationId && open;
            if (!isFromMe && !isActiveThread) {
                setConversations((prev) => {
                    const list = Array.isArray(prev) ? prev : [];
                    let updated = false;
                    const next = list.map((c) => {
                        const cid = Number(c?.conversation_id);
                        const oid = Number(c?.other_user?.user_id);
                        if (!Number.isFinite(oid) || !Number.isFinite(cid)) return c;
                        if (cid !== conversationId && oid !== senderId) return c;

                        updated = true;
                        const prevUnread = Number(c?.unread_count || 0);
                        return {
                            ...c,
                            unread_count: prevUnread + 1,
                            last_message: {
                                id: Number(msg.id),
                                sender_user_id: Number(msg.sender_user_id),
                                body_text: String(msg.body_text || ''),
                                created_at: msg.created_at || null
                            },
                            last_message_at: msg.created_at || c?.last_message_at || null
                        };
                    });
                    return updated ? next : list;
                });
            }

            // Refresh conversation list (unread counts + last message).
            loadConversations({ silent: true });
        });

        s.on('conversation:read', (payload) => {
            const conversationId = Number(payload?.conversation_id);
            const userId = Number(payload?.user_id);
            const lastRead = Number(payload?.last_read_message_id);
            if (!Number.isFinite(conversationId) || !Number.isFinite(userId) || !Number.isFinite(lastRead)) return;

            const otherId = activeUserIdRef.current;

            setThread((prev) => {
                if (Number(prev?.conversation_id) !== conversationId) return prev;
                if (otherId && userId === Number(otherId)) {
                    return { ...prev, other_last_read_message_id: Math.max(Number(prev.other_last_read_message_id || 0), lastRead) };
                }
                return prev;
            });

            setConversations((prev) => (prev || []).map((c) => {
                if (Number(c?.conversation_id) !== conversationId) return c;
                const otherId = Number(c?.other_user?.user_id);
                if (Number.isFinite(otherId) && userId === otherId) {
                    return { ...c, other_last_read_message_id: Math.max(Number(c.other_last_read_message_id || 0), lastRead) };
                }
                return c;
            }));
        });

        return () => {
            try { s.disconnect(); } catch { }
            socketRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    async function loadRoster({ silent } = {}) {
        if (!silent) {
            setRosterLoading(true);
            setRosterError('');
        }
        try {
            const json = await apiGet(ROSTER_PATHS, (path) => `${API_BASE_URL}${path}`);
            const items = Array.isArray(json?.items) ? json.items : [];
            setRoster(items);
        } catch (e) {
            if (!silent) setRosterError(String(e?.message || 'Failed to load team roster'));
        } finally {
            if (!silent) setRosterLoading(false);
        }
    }

    async function loadConversations({ silent } = {}) {
        if (!silent) {
            setConvLoading(true);
            setConvError('');
        }
        try {
            const json = await apiGet(CONVERSATIONS_PATHS, (path) => `${API_BASE_URL}${path}`);
            const items = Array.isArray(json?.items) ? json.items : [];
            setConversations(items);
        } catch (e) {
            if (!silent) setConvError(String(e?.message || 'Failed to load conversations'));
        } finally {
            if (!silent) setConvLoading(false);
        }
    }

    async function openThread(otherUserId) {
        const id = Number(otherUserId);
        if (!Number.isFinite(id)) return;

        // Optimistically clear unread marker as soon as the user opens the thread.
        setConversations((prev) => (Array.isArray(prev) ? prev : []).map((c) => {
            const otherId = Number(c?.other_user?.user_id);
            if (!Number.isFinite(otherId)) return c;
            if (otherId !== id) return c;
            return { ...c, unread_count: 0 };
        }));

        setActiveUserId(id);
        setThreadLoading(true);
        setThreadError('');
        setTypingFromOther(false);

        try {
            const paths = buildUserMessagesPaths(id);
            const url = (path) => `${API_BASE_URL}${path}?limit=80`;
            const json = await apiGet(paths, (path) => url(path));
            setThread({
                conversation_id: json?.conversation_id ?? null,
                other_user: json?.other_user || (rosterById.get(id) || null),
                my_last_read_message_id: Number(json?.my_last_read_message_id || 0),
                other_last_read_message_id: Number(json?.other_last_read_message_id || 0),
                items: Array.isArray(json?.items) ? json.items : []
            });

            const items = Array.isArray(json?.items) ? json.items : [];
            const last = items.length ? items[items.length - 1] : null;
            if (last && Number(last.sender_user_id) === id) {
                await markRead(id, Number(last.id), { silent: true });
            }

            // Conversation list unread counts should update quickly when opening.
            loadConversations({ silent: true });
        } catch (e) {
            setThreadError(String(e?.message || 'Failed to load messages'));
            setThread({ conversation_id: null, other_user: rosterById.get(id) || null, my_last_read_message_id: 0, other_last_read_message_id: 0, items: [] });
        } finally {
            setThreadLoading(false);
        }
    }

    async function sendMessage() {
        const otherId = Number(activeUserId);
        const body = String(draft || '').trim();
        if (!otherId || !body) return;

        setDraft('');
        emitTyping(otherId, false);

        try {
            const paths = buildUserSendMessagePaths(otherId);
            const json = await apiPost(paths, (path) => `${API_BASE_URL}${path}`, { body });

            const msg = json?.message;
            if (msg?.id) {
                setThread((prev) => {
                    const existing = (prev?.items || []).some((m) => Number(m.id) === Number(msg.id));
                    if (existing) return prev;
                    return { ...prev, conversation_id: json?.conversation_id ?? prev.conversation_id, items: [...(prev.items || []), msg] };
                });
            }

            loadConversations({ silent: true });
        } catch (e) {
            setThreadError(String(e?.message || 'Failed to send message'));
            setDraft(body);
        }
    }

    async function markRead(otherUserId, upToMessageId, { silent } = {}) {
        const otherId = Number(otherUserId);
        const msgId = Number(upToMessageId);
        if (!Number.isFinite(otherId) || !Number.isFinite(msgId) || msgId < 0) return;

        try {
            const paths = buildUserReadPaths(otherId);
            const json = await apiPost(paths, (path) => `${API_BASE_URL}${path}`, { last_read_message_id: msgId });
            const newVal = Number(json?.last_read_message_id || msgId);
            setThread((prev) => ({ ...prev, my_last_read_message_id: Math.max(Number(prev.my_last_read_message_id || 0), newVal) }));
            if (!silent) loadConversations({ silent: true });
        } catch {
            // ignore
        }
    }

    function emitTyping(otherUserId, isTyping) {
        const s = socketRef.current;
        if (!s) return;
        const otherId = Number(otherUserId);
        if (!Number.isFinite(otherId)) return;

        if (isTyping) {
            const now = Date.now();
            if (now - lastTypingEmitRef.current < 900) return;
            lastTypingEmitRef.current = now;
        }

        try {
            s.emit('typing', { to_user_id: otherId, is_typing: Boolean(isTyping) });
        } catch {
            // ignore
        }
    }

    const rosterItems = useMemo(() => {
        const items = Array.isArray(roster) ? roster : [];
        const sorted = [...items]
            .filter((u) => Number.isFinite(Number(u?.user_id)))
            .sort((a, b) => {
                const ao = a?.online ? 1 : 0;
                const bo = b?.online ? 1 : 0;
                if (ao !== bo) return bo - ao;
                const an = safeName(a).toLowerCase();
                const bn = safeName(b).toLowerCase();
                return an.localeCompare(bn);
            });
        return sorted;
    }, [roster]);

    const unreadCountsByUserId = useMemo(() => {
        const map = {};
        const list = Array.isArray(conversations) ? conversations : [];
        for (const c of list) {
            const otherId = Number(c?.other_user?.user_id);
            const unread = Number(c?.unread_count || 0);
            if (!Number.isFinite(otherId) || unread <= 0) continue;
            map[String(otherId)] = unread;
        }
        return map;
    }, [conversations]);

    const hasAnyUnread = useMemo(() => {
        return Object.values(unreadCountsByUserId).some((n) => Number(n) > 0);
    }, [unreadCountsByUserId]);

    if (!hasSession) return null;

    const activeOtherId = activeUserId ? Number(activeUserId) : null;
    const otherLastRead = Number(thread.other_last_read_message_id || 0);
    const launcherHasUnread = !open && hasAnyUnread;

    return (
        <>
            <Badge
                color="error"
                variant="dot"
                overlap="circular"
                invisible={open || !hasAnyUnread}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{
                    position: 'fixed',
                    bottom,
                    right: 32,
                    zIndex: 1100,
                    '& .MuiBadge-badge': {
                        top: launcherHasUnread ? 9 : 6,
                        right: launcherHasUnread ? 9 : 6,
                        width: launcherHasUnread ? 16 : 8,
                        height: launcherHasUnread ? 16 : 8,
                        minWidth: launcherHasUnread ? 16 : 8,
                        transform: 'none',
                        borderRadius: '999px',
                        border: '3px solid',
                        borderColor: 'background.paper',
                        boxShadow: launcherHasUnread ? '0 0 0 4px rgba(239, 68, 68, 0.18)' : 'none'
                    }
                }}
            >
                <Fab
                    aria-label="Team Messenger"
                    onClick={(e) => {
                        if (open) {
                            closeChat();
                            return;
                        }
                        setAnchorEl(e.currentTarget);
                        setOpen(true);
                    }}
                    sx={{
                        position: 'static',
                        width: 68,
                        height: 68,
                        color: '#f8fafc',
                        background: 'linear-gradient(145deg, #0f172a 0%, #1d4ed8 48%, #0f766e 100%)',
                        border: '1px solid rgba(255,255,255,0.16)',
                        boxShadow: launcherHasUnread
                            ? '0 24px 50px rgba(15, 23, 42, 0.34), 0 0 0 10px rgba(59, 130, 246, 0.12)'
                            : '0 20px 42px rgba(15, 23, 42, 0.28)',
                        overflow: 'visible',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            inset: -6,
                            borderRadius: '50%',
                            border: launcherHasUnread ? '1px solid rgba(248, 250, 252, 0.34)' : '1px solid rgba(248, 250, 252, 0.16)',
                            opacity: launcherHasUnread ? 1 : 0.72
                        },
                        '&:hover': {
                            background: 'linear-gradient(145deg, #111827 0%, #2563eb 52%, #0f766e 100%)'
                        }
                    }}
                >
                    <ChatBubbleOutlineIcon sx={{ fontSize: 28 }} />
                </Fab>
            </Badge>

            <Popper
                open={Boolean(open && anchorEl)}
                anchorEl={anchorEl}
                placement="top-end"
                modifiers={[{ name: 'offset', options: { offset: [0, 12] } }]}
                sx={{ zIndex: (t) => t.zIndex.modal + 1 }}
            >
                <ClickAwayListener onClickAway={closeChat}>
                    <Paper
                        sx={{
                            width: {
                                xs: 'calc(100vw - 24px)',
                                sm: 'min(760px, calc(100vw - 36px))'
                            },
                            height: 'min(560px, calc(100vh - 140px))',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            borderRadius: 5,
                            border: '1px solid',
                            borderColor: (theme) => alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.12 : 0.7),
                            background: (theme) => theme.palette.mode === 'dark'
                                ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(15, 23, 42, 0.9) 100%)'
                                : 'linear-gradient(180deg, rgba(255, 255, 255, 0.97) 0%, rgba(241, 245, 249, 0.98) 100%)',
                            boxShadow: (theme) => theme.palette.mode === 'dark'
                                ? '0 28px 90px rgba(2, 6, 23, 0.54)'
                                : '0 28px 72px rgba(15, 23, 42, 0.22)',
                            backdropFilter: 'blur(18px)',
                            WebkitBackdropFilter: 'blur(18px)'
                        }}
                    >
                        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Box
                                sx={{
                                    px: 2.25,
                                    py: 1.75,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1.5,
                                    background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.96) 0%, rgba(30, 64, 175, 0.9) 54%, rgba(15, 118, 110, 0.78) 100%)',
                                    color: '#f8fafc'
                                }}
                            >
                                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                                    <Box
                                        sx={{
                                            width: 42,
                                            height: 42,
                                            borderRadius: 2.5,
                                            display: 'grid',
                                            placeItems: 'center',
                                            background: 'linear-gradient(145deg, rgba(255,255,255,0.22) 0%, rgba(191,219,254,0.18) 100%)',
                                            border: '1px solid rgba(255,255,255,0.18)',
                                            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.14)'
                                        }}
                                    >
                                        <ChatBubbleOutlineIcon sx={{ fontSize: 22 }} />
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ fontWeight: 900, letterSpacing: '-0.01em' }}>Team Messenger</Typography>
                                        <Typography variant="caption" sx={{ color: 'rgba(248, 250, 252, 0.8)' }}>
                                            Direct team messages, unread cues, and live typing status.
                                        </Typography>
                                    </Box>
                                </Stack>
                                <IconButton onClick={closeChat} aria-label="Close messenger">
                                    <CloseIcon sx={{ color: '#f8fafc' }} />
                                </IconButton>
                            </Box>
                            <Divider sx={{ borderColor: (theme) => alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.1 : 0.65) }} />

                            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
                                <Box
                                    sx={{
                                        width: 220,
                                        borderRight: 1,
                                        borderColor: 'divider',
                                        display: { xs: 'none', sm: 'flex' },
                                        flexDirection: 'column',
                                        minHeight: 0,
                                        bgcolor: (theme) => theme.palette.mode === 'dark'
                                            ? alpha('#020617', 0.26)
                                            : alpha('#e2e8f0', 0.34)
                                    }}
                                >
                                    <Box sx={{ px: 1.75, py: 1.25 }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Team</Typography>
                                    </Box>

                                    {(rosterLoading || convLoading) && !roster.length && !conversations.length ? (
                                        <Box sx={{ px: 2, py: 2, display: 'flex', justifyContent: 'center' }}>
                                            <CircularProgress size={18} />
                                        </Box>
                                    ) : null}

                                    {(rosterError || convError) ? (
                                        <Box sx={{ px: 1.5, py: 1 }}>
                                            <Typography variant="caption" sx={{ color: 'error.main' }}>
                                                {rosterError || convError}
                                            </Typography>
                                        </Box>
                                    ) : null}

                                    <List dense sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 1, pb: 1 }}>
                                        {rosterItems.map((u) => {
                                            const id = Number(u.user_id);
                                            const name = safeName(u);
                                            const selected = activeOtherId && id === activeOtherId;
                                            const hasUnreadFromThisUser = Number(unreadCountsByUserId[String(id)] || 0) > 0;
                                            return (
                                                <ListItemButton
                                                    key={`u:${id}`}
                                                    selected={selected}
                                                    onClick={() => openThread(id)}
                                                    sx={{
                                                        mb: 0.5,
                                                        borderRadius: 3,
                                                        px: 1,
                                                        py: 0.8,
                                                        border: '1px solid transparent',
                                                        '&.Mui-selected': {
                                                            bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
                                                            borderColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.34 : 0.18)
                                                        },
                                                        '&:hover': {
                                                            bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.14 : 0.08)
                                                        }
                                                    }}
                                                >
                                                    <ListItemAvatar>
                                                        <Badge
                                                            color="error"
                                                            variant="dot"
                                                            overlap="circular"
                                                            invisible={!hasUnreadFromThisUser}
                                                            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                                            sx={{
                                                                '& .MuiBadge-badge': {
                                                                    width: 10,
                                                                    height: 10,
                                                                    minWidth: 10,
                                                                    border: '2px solid',
                                                                    borderColor: 'background.paper'
                                                                }
                                                            }}
                                                        >
                                                            <Badge
                                                                color="success"
                                                                variant="dot"
                                                                overlap="circular"
                                                                invisible={!u?.online}
                                                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                                sx={{
                                                                    '& .MuiBadge-badge': {
                                                                        width: 10,
                                                                        height: 10,
                                                                        minWidth: 10,
                                                                        border: '2px solid',
                                                                        borderColor: 'background.paper'
                                                                    }
                                                                }}
                                                            >
                                                                <Avatar src={u?.profile_photo_url || undefined}>{initials(name)}</Avatar>
                                                            </Badge>
                                                        </Badge>
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={<Typography variant="body2" noWrap sx={{ fontWeight: 800 }}>{name}</Typography>}
                                                        secondary={u?.online ? 'Available now' : 'Offline'}
                                                        secondaryTypographyProps={{
                                                            variant: 'caption',
                                                            noWrap: true,
                                                            sx: { color: 'text.secondary' }
                                                        }}
                                                    />
                                                </ListItemButton>
                                            );
                                        })}

                                        {!rosterItems.length && !rosterLoading ? (
                                            <Box sx={{ px: 1.5, py: 1 }}>
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>No team members found.</Typography>
                                            </Box>
                                        ) : null}
                                    </List>
                                </Box>

                                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                                    {/* Mobile roster bar (avatars only) */}
                                    <Box
                                        sx={{
                                            display: { xs: 'flex', sm: 'none' },
                                            px: 1.25,
                                            py: 1,
                                            gap: 1,
                                            overflowX: 'auto',
                                            borderBottom: 1,
                                            borderColor: 'divider',
                                            WebkitOverflowScrolling: 'touch'
                                        }}
                                    >
                                        {rosterItems.map((u) => {
                                            const id = Number(u.user_id);
                                            const name = safeName(u);
                                            const selected = activeOtherId && id === activeOtherId;
                                            const hasUnreadFromThisUser = Number(unreadCountsByUserId[String(id)] || 0) > 0;
                                            return (
                                                <IconButton
                                                    key={`mbar:${id}`}
                                                    size="small"
                                                    onClick={() => openThread(id)}
                                                    sx={{ p: 0.25 }}
                                                    aria-label={`Message ${name}`}
                                                >
                                                    <Badge
                                                        color="error"
                                                        variant="dot"
                                                        overlap="circular"
                                                        invisible={!hasUnreadFromThisUser}
                                                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                                        sx={{
                                                            '& .MuiBadge-badge': {
                                                                width: 10,
                                                                height: 10,
                                                                minWidth: 10,
                                                                border: '2px solid',
                                                                borderColor: 'background.paper'
                                                            }
                                                        }}
                                                    >
                                                        <Badge
                                                            color="success"
                                                            variant="dot"
                                                            overlap="circular"
                                                            invisible={!u?.online}
                                                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                            sx={{
                                                                '& .MuiBadge-badge': {
                                                                    width: 10,
                                                                    height: 10,
                                                                    minWidth: 10,
                                                                    border: '2px solid',
                                                                    borderColor: 'background.paper'
                                                                }
                                                            }}
                                                        >
                                                            <Avatar
                                                                src={u?.profile_photo_url || undefined}
                                                                sx={{
                                                                    width: 34,
                                                                    height: 34,
                                                                    border: selected ? '2px solid' : '2px solid transparent',
                                                                    borderColor: selected ? 'primary.main' : 'transparent'
                                                                }}
                                                            >
                                                                {initials(name)}
                                                            </Avatar>
                                                        </Badge>
                                                    </Badge>
                                                </IconButton>
                                            );
                                        })}

                                        {!rosterItems.length && !rosterLoading ? (
                                            <Typography variant="caption" sx={{ color: 'text.secondary', px: 0.5 }}>
                                                No team members found.
                                            </Typography>
                                        ) : null}
                                    </Box>

                                    <Box
                                        sx={{
                                            px: 2.25,
                                            py: 1.5,
                                            borderBottom: 1,
                                            borderColor: 'divider',
                                            background: (theme) => theme.palette.mode === 'dark'
                                                ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.24) 0%, rgba(15, 23, 42, 0.08) 100%)'
                                                : 'linear-gradient(180deg, rgba(255, 255, 255, 0.72) 0%, rgba(248, 250, 252, 0.38) 100%)'
                                        }}
                                    >
                                        <Typography sx={{ fontWeight: 900 }} noWrap>
                                            {activeUser ? safeName(activeUser) : 'Select a team member'}
                                        </Typography>
                                        {activeUser ? (
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                {activeUser?.online ? 'Online' : 'Offline'}{typingFromOther ? ' • typing…' : ''}
                                            </Typography>
                                        ) : null}
                                    </Box>

                                    {threadError ? (
                                        <Box sx={{ px: 2, py: 1 }}>
                                            <Typography variant="caption" sx={{ color: 'error.main' }}>{threadError}</Typography>
                                        </Box>
                                    ) : null}

                                    <Box
                                        ref={scrollRef}
                                        sx={{
                                            flex: 1,
                                            minHeight: 0,
                                            overflow: 'auto',
                                            px: 2.25,
                                            py: 1.75,
                                            background: (theme) => theme.palette.mode === 'dark'
                                                ? 'radial-gradient(circle at top, rgba(37, 99, 235, 0.14) 0%, rgba(2, 6, 23, 0) 42%), linear-gradient(180deg, rgba(2, 6, 23, 0.18) 0%, rgba(15, 23, 42, 0.08) 100%)'
                                                : 'radial-gradient(circle at top, rgba(59, 130, 246, 0.1) 0%, rgba(255, 255, 255, 0) 38%), linear-gradient(180deg, rgba(248, 250, 252, 0.92) 0%, rgba(241, 245, 249, 0.88) 100%)'
                                        }}
                                    >
                                        {!activeUser ? (
                                            <Paper
                                                variant="outlined"
                                                sx={{
                                                    p: 2.25,
                                                    borderRadius: 4,
                                                    bgcolor: (theme) => alpha(theme.palette.background.paper, 0.82),
                                                    borderColor: 'divider',
                                                    boxShadow: '0 16px 30px rgba(15, 23, 42, 0.08)'
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                    Start a 1:1 chat with someone in your team.
                                                </Typography>
                                            </Paper>
                                        ) : threadLoading ? (
                                            <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                                                <CircularProgress size={20} />
                                            </Box>
                                        ) : (
                                            <Stack spacing={1.25}>
                                                {(thread.items || []).map((m) => {
                                                    const senderId = Number(m.sender_user_id);
                                                    const isMine = activeOtherId ? senderId !== activeOtherId : true;
                                                    const created = formatTimeShort(m.created_at);
                                                    const isRead = isMine && otherLastRead && Number(m.id) <= otherLastRead;

                                                    return (
                                                        <Box key={m.id} sx={{ display: 'flex', justifyContent: isMine ? 'flex-end' : 'flex-start' }}>
                                                            <Box sx={{ maxWidth: '82%' }}>
                                                                <Paper
                                                                    sx={{
                                                                        p: 1.35,
                                                                        borderRadius: isMine ? '22px 22px 8px 22px' : '22px 22px 22px 8px',
                                                                        bgcolor: isMine ? 'transparent' : (theme) => alpha(theme.palette.background.paper, 0.9),
                                                                        background: isMine
                                                                            ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 52%, #0f766e 100%)'
                                                                            : (theme) => theme.palette.mode === 'dark'
                                                                                ? 'linear-gradient(180deg, rgba(30, 41, 59, 0.96) 0%, rgba(15, 23, 42, 0.9) 100%)'
                                                                                : 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.94) 100%)',
                                                                        color: isMine ? '#eff6ff' : 'text.primary',
                                                                        border: '1px solid',
                                                                        borderColor: isMine ? alpha('#60a5fa', 0.26) : 'divider',
                                                                        boxShadow: isMine
                                                                            ? '0 16px 30px rgba(37, 99, 235, 0.22)'
                                                                            : '0 12px 24px rgba(15, 23, 42, 0.08)'
                                                                    }}
                                                                >
                                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.body_text}</Typography>
                                                                </Paper>
                                                                <Stack direction="row" spacing={1} sx={{ mt: 0.25, justifyContent: isMine ? 'flex-end' : 'flex-start' }}>
                                                                    {created ? (
                                                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{created}</Typography>
                                                                    ) : null}
                                                                    {isRead ? (
                                                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Read</Typography>
                                                                    ) : null}
                                                                </Stack>
                                                            </Box>
                                                        </Box>
                                                    );
                                                })}
                                            </Stack>
                                        )}
                                    </Box>

                                    <Divider sx={{ borderColor: (theme) => alpha(theme.palette.divider, 0.8) }} />

                                    <Box
                                        sx={{
                                            px: 2.25,
                                            py: 1.5,
                                            background: (theme) => theme.palette.mode === 'dark'
                                                ? alpha('#020617', 0.16)
                                                : alpha('#ffffff', 0.82)
                                        }}
                                    >
                                        <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-end' }}>
                                            <TextField
                                                label={activeUser ? 'Message' : 'Message'}
                                                placeholder={activeUser ? 'Type a message…' : 'Select someone to start'}
                                                value={draft}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    setDraft(v);
                                                    if (activeOtherId) emitTyping(activeOtherId, Boolean(String(v || '').trim()));
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                        e.preventDefault();
                                                        sendMessage();
                                                    }
                                                }}
                                                minRows={1}
                                                maxRows={4}
                                                multiline
                                                fullWidth
                                                disabled={!activeUser}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 4,
                                                        bgcolor: (theme) => theme.palette.mode === 'dark'
                                                            ? alpha('#0f172a', 0.6)
                                                            : alpha('#ffffff', 0.92),
                                                        boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)'
                                                    }
                                                }}
                                            />
                                            <IconButton
                                                aria-label="Send message"
                                                onClick={sendMessage}
                                                disabled={!activeUser || !String(draft || '').trim()}
                                                sx={{
                                                    width: 48,
                                                    height: 48,
                                                    color: '#eff6ff',
                                                    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 52%, #0f766e 100%)',
                                                    boxShadow: '0 16px 28px rgba(37, 99, 235, 0.24)',
                                                    '&:hover': {
                                                        background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 52%, #0f766e 100%)'
                                                    },
                                                    '&.Mui-disabled': {
                                                        color: 'rgba(248, 250, 252, 0.72)',
                                                        background: 'rgba(148, 163, 184, 0.42)'
                                                    }
                                                }}
                                            >
                                                <SendIcon />
                                            </IconButton>
                                        </Stack>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </>
    );
}
